import { SKIP_RENDER } from '@mediashop/app/constants/semanticConstants';
import classnames from 'classnames';
import Currency from '../../../../atom/Currency';
import { VariantOptionProps } from './index';
import { Variant } from '@mediashop/app/api/types/ClientProduct';
import { useCheaperSet } from '@mediashop/app/hooks/useCheaperSet';
import { DetailedVariant } from '@mediashop/app/api/types/ClientProductDetailed';

const checkIfDisabled = (
    isSubScription: boolean,
    variant: Variant | undefined,
    variants: Array<Variant>,
    dimension: string,
    option: string
): Variant | undefined => {
    let disabledVariant: Variant | undefined;
    const tempSku: string | undefined = isSubScription
        ? ((variant as DetailedVariant)?.attributes.subscriptionVariantSku ?? variant?.sku)
        : ((variant as DetailedVariant)?.attributes.subscriptionBaseVariantSku ?? variant?.sku);

    const foundBaseVariant = variants.find((currVariant) => currVariant.sku === tempSku);

    if (!foundBaseVariant) {
        disabledVariant = variant;
    }

    if (!variant) {
        disabledVariant = variants.find((variant) => variant.attributes[dimension] === option);
    }

    return disabledVariant;
};

const componentName = 'variant-option';

const VariantOption = ({
    dimension,
    lastSelectedDimension,
    onClick,
    option,
    selectedDimensions,
    selectedVariant,
    type,
    variantDimensions,
    variants,
}: VariantOptionProps): JSX.Element => {
    const isActive = selectedVariant?.attributes[dimension] === option;
    const isSubScriptionSelected = Boolean((selectedVariant as DetailedVariant)?.attributes.subscriptionBaseVariantSku);
    let variant: Variant | undefined;
    if (isActive) {
        variant = selectedVariant;
    } else {
        const filteredVariants = variants.filter(
            (variant) =>
                (isSubScriptionSelected && (variant as DetailedVariant).attributes.subscriptionBaseVariantSku) ||
                (!isSubScriptionSelected && !(variant as DetailedVariant).attributes.subscriptionBaseVariantSku)
        );

        variant = filteredVariants.find((variant) => {
            if (variantDimensions.length > 1) {
                if (lastSelectedDimension === dimension) {
                    const notSelectedDimension = variantDimensions.find((vDimension) => vDimension.key !== dimension);
                    return (
                        notSelectedDimension &&
                        variant.attributes[dimension] === option &&
                        variant.attributes[notSelectedDimension.key] === selectedDimensions[notSelectedDimension.key]
                    );
                }
                return (
                    variant.attributes[dimension] === option &&
                    variant.attributes[lastSelectedDimension] === selectedDimensions[lastSelectedDimension]
                );
            }

            return variant.attributes[dimension] === option;
        });
    }

    const disabledVariant = checkIfDisabled(isSubScriptionSelected, variant, variants, dimension, option);

    const { showCheaperSetPrice, cheaperSetPrice } = useCheaperSet(variant);

    const onOptionClick = () => {
        if (variant && !isActive) {
            onClick(dimension, option);
        } else if (disabledVariant) {
            onClick(dimension, option, disabledVariant);
        }
    };

    return (
        <div
            className={classnames(componentName, `${componentName}--${type}`, {
                [`${componentName}--active`]: isActive,
                [`${componentName}--disabled`]: disabledVariant,
                [`${componentName}--promotion`]: variant?.price?.priceType === 'DEAL' || showCheaperSetPrice,
            })}
            onClick={() => onOptionClick()}
            data-testid={componentName}
        >
            <div className={`${componentName}__title`}>{option}</div>
            {variant?.price?.centAmount && type === 'badge' ? (
                <div className={`${componentName}__price`}>
                    <Currency price={showCheaperSetPrice ? cheaperSetPrice : variant.price} />
                </div>
            ) : (
                SKIP_RENDER
            )}
        </div>
    );
};

export default VariantOption;
